import React from "react";
import { ArrowRight, Code2, Zap } from "lucide-react";
import { useLeadModal } from "./LeadModalContext";

/**
 * Promotes the software-only tier — for venues / agencies who already have
 * hardware (cameras, kiosks, ring lights) and just want the Stori AI layer.
 */
export default function SoftwareOnlyCTA() {
  const { openModal } = useLeadModal();

  return (
    <section
      data-testid="software-only-cta"
      className="relative py-20 lg:py-28 border-b border-stori-border overflow-hidden"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 grid-noise opacity-30 pointer-events-none" />
      <div className="absolute -top-32 right-0 w-[600px] h-[600px] bg-stori-cyan/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-32 left-0 w-[500px] h-[500px] bg-stori-cyan/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="rounded-3xl border border-stori-cyan/30 bg-gradient-to-br from-stori-surface1 to-stori-surface2 p-8 lg:p-14 cyan-glow">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
              <div className="label-eyebrow text-stori-cyan flex items-center gap-2">
                <span className="inline-block h-px w-8 bg-stori-cyan" />
                SOFTWARE-ONLY · NO HARDWARE NEEDED
              </div>
              <h2
                data-testid="software-only-headline"
                className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]"
              >
                Already have the hardware?
                <br />
                <span className="text-stori-cyan cyan-text-glow">Run Stori as software-only.</span>
              </h2>
              <p className="mt-6 text-lg text-stori-muted max-w-xl leading-relaxed">
                Plug Stori into your existing kiosks, cameras, or ring lights and unlock the
                full AI layer — playbooks, influencer detection, CRM sync — without buying a
                single piece of new hardware.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => openModal("trial_signup")}
                  data-testid="software-only-cta-button"
                  className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
                >
                  Get the software-only plan
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => openModal("demo_request")}
                  data-testid="software-only-events-team-button"
                  className="group inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan font-semibold px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors"
                >
                  Book our live events team
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
              <p className="mt-5 text-sm text-stori-muted">
                DIY with our software, or hand it off — our crew arrives, sets up, and runs the activation for you.
              </p>
            </div>

            {/* Right: feature list */}
            <div className="lg:col-span-5">
              <ul className="space-y-3">
                {[
                  { icon: Zap, label: "10-minute setup", desc: "Deploy via SDK or web kiosk" },
                  { icon: Code2, label: "Works with your stack", desc: "iPad, web, custom hardware — all supported" },
                  { icon: ArrowRight, label: "Full feature parity", desc: "Same AI, same playbooks, same CRM sync" },
                ].map(({ icon: Icon, label, desc }) => (
                  <li
                    key={label}
                    className="flex items-start gap-4 p-4 rounded-xl border border-stori-border bg-stori-bg/40"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-md border border-stori-cyan/40 text-stori-cyan flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="font-heading text-base font-semibold text-white">{label}</div>
                      <div className="text-sm text-stori-muted leading-snug">{desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
